<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useNIRApplicationsStore } from "@/domains/home/services/nir/registration/stores";
import FormDataViewer from "@/domains/home/services/nir/forms/components/FormDataViewer.vue";
import { useFormsStore } from "@/domains/home/services/nir/configurations/stores/forms.ts";
import { useInstitutionStore } from "@/domains/home/services/nir/associate-registers/stores";

const loading = ref(true);
const store = useNIRApplicationsStore()
const formStore = useFormsStore()
const institutionStore = useInstitutionStore()
const activeTab = ref<'form' | 'attachments'>('form')

const props = defineProps({
  id:String,
})

onMounted(() => {
  institutionStore.fetchInstitutions(1, 1000)
  store.fetchTask(props.id || "")
      .then(() =>{
        loading.value = false
        formStore.fetchForm(store.vtask?.formId || "")
        formStore.fetchFormFields(store.vtask?.formId || "")
      })
})

</script>

<template>
  <div class="flex flex-col">
    <!-- Application Details - Always Visible -->
    <div class="flex flex-col bg-white rounded-lg shadow p-6 mb-4" id="details">
      <h3 class="text-xl font-semibold text-gray-900 mb-4 border-b pb-2">
        <i class="fa fa-info-circle text-primary mr-2"></i>
        Application Details
      </h3>

      <div v-if="store.vtask" class="grid grid-cols-2 gap-4">
        <!-- Tracking Number -->
        <div v-if="store.vtask.trackingNumber" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Tracking Number</label>
          <p class="text-sm text-gray-900 font-mono">{{ store.vtask.trackingNumber }}</p>
        </div>

        <div v-if="store.vtask.interimErn" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1"
          >Interim ERN</label
          >
          <p class="text-sm text-gray-900 font-semibold">{{ store.vtask.interimErn }}</p>
        </div>

        <!-- Name -->
        <div v-if="store.vtask.name" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Entity Name</label>
          <p class="text-sm text-gray-900 font-semibold">{{ store.vtask.name }}</p>
        </div>

        <!-- Type -->
        <div v-if="store.vtask.type" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Type</label>
          <p class="text-sm text-gray-900">{{ store.vtask.type }}</p>
        </div>

        <!-- Sub Type -->
        <div v-if="store.vtask.subType" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Sub Type</label>
          <p class="text-sm text-gray-900">{{ store.vtask.subType }}</p>
        </div>

        <!-- Form ID -->
        <div v-if="store.vtask.formId" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Form</label>
          <p class="text-sm text-gray-900 font-mono">{{ formStore.form?.name || '' }}</p>
        </div>

        <!-- Institution ID -->
        <div v-if="store.vtask.institutionId" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Institution</label>
          <p class="text-sm text-gray-900 font-mono" v-if="store.vtask?.institutionId">{{ institutionStore.institutions?.find((i => i.id === store.vtask?.institutionId))?.name || 'N/A' }}</p>
        </div>

        <!-- Status -->
        <div v-if="store.vtask.status" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Status</label>
          <span class="text-sm font-medium px-3 py-1 rounded-full w-fit"
                :class="{
                  'bg-gray-100 text-gray-600': store.vtask.status === 'PENDING',
                  'bg-gray-100 text-gray-800': store.vtask.status === 'SUBMITTED',
                  'bg-success-50 text-success-600 border border-success-200': store.vtask.status === 'PREAPPROVAL',
                  'bg-primary-100 text-primary-800': store.vtask.status === 'ASSIGNED',
                  'bg-success-100 text-success-800': store.vtask.status === 'APPROVED',
                  'bg-danger-100 text-danger-800': store.vtask.status === 'REJECTED',
                  'bg-warning-100 text-warning-800': store.vtask.status === 'QUERIED',
                }">
            {{ store.vtask.status }}
          </span>
        </div>

        <!-- Created At -->
        <div v-if="store.vtask.createdAt" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Created At</label>
          <p class="text-sm text-gray-900">{{ new Date(store.vtask.createdAt).toLocaleString() }}</p>
        </div>

        <!-- Updated At -->
        <div v-if="store.vtask.updatedAt" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Updated At</label>
          <p class="text-sm text-gray-900">{{ new Date(store.vtask.updatedAt).toLocaleString() }}</p>
        </div>

        <!-- Approved At -->
        <div v-if="store.vtask.approvedAt" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Approved At</label>
          <p class="text-sm text-gray-900">{{ new Date(store.vtask.approvedAt).toLocaleString() }}</p>
        </div>

        <!-- Rejected At -->
        <div v-if="store.vtask.rejectedAt" class="flex flex-col">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Rejected At</label>
          <p class="text-sm text-gray-900">{{ new Date(store.vtask.rejectedAt).toLocaleString() }}</p>
        </div>

        <!-- Message -->
        <div v-if="store.vtask.message" class="flex flex-col md:col-span-2">
          <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Message</label>
          <p class="text-sm text-gray-900 bg-gray-50 p-3 rounded border border-gray-200">{{ store.vtask.message }}</p>
        </div>
      </div>

      <div v-else class="text-center py-8 text-gray-500">
        <i class="fa fa-inbox text-4xl mb-2"></i>
        <p>No task details available</p>
      </div>
    </div>

    <!-- Tab Navigation -->
    <div class="flex border-b border-gray-200 mb-4">
      <label
          @click="activeTab = 'form'"
          :class="[
            'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
            activeTab === 'form'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          ]"
      >
        <i class="fa fa-file-alt mr-2"></i>
        Form Data
      </label>
      <label
          @click="activeTab = 'attachments'"
          :class="[
            'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
            activeTab === 'attachments'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          ]"
      >
        <i class="fa fa-paperclip mr-2"></i>
        Attachments
        <span v-if="store.vtask?.attachments?.length" class="ml-2 bg-primary-100 text-primary-800 text-xs font-medium px-2 py-0.5 rounded-full">
          {{ store.vtask.attachments.length }}
        </span>
      </label>
    </div>

    <!-- Form Data Tab -->
    <div v-show="activeTab === 'form'">
      <FormDataViewer v-if="store.vtask && formStore.formData" :form-data="formStore.formData" :value-data="store.vtask.data"/>
    </div>

    <!-- Attachments Tab -->
    <div v-show="activeTab === 'attachments'">
      <div class="flex flex-col bg-white rounded-lg shadow p-6">
        <h3 class="text-xl font-semibold text-gray-900 mb-4 border-b pb-2">
          <i class="fa fa-paperclip text-primary mr-2"></i>
          Application Attachments
        </h3>

        <div v-if="store.vtask?.attachments?.length" class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
              v-for="attachment in store.vtask.attachments"
              :key="attachment.id"
              class="flex flex-col bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
          >
            <div class="flex items-start justify-between">
              <div class="flex items-center">
                <div class="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                  <i class="fa fa-file text-primary"></i>
                </div>
                <div>
                  <h4 class="text-sm font-semibold text-gray-900">{{ attachment.title }}</h4>
                  <p class="text-xs text-gray-500 mt-0.5" v-if="attachment.description">{{ attachment.description }}</p>
                </div>
              </div>
            </div>

            <div class="mt-3 pt-3 border-t border-gray-200">
              <div class="flex flex-col space-y-1">
                <div class="flex flex-col text-xs text-gray-500">
                  <span class="font-bold">File ID</span>
                  <span class="font-mono truncate" :title="attachment.fileId">{{ attachment.fileId }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="text-center py-12 text-gray-500">
          <i class="fa fa-folder-open text-4xl mb-3 text-gray-300"></i>
          <p class="text-gray-600">No attachments available for this application</p>
        </div>
      </div>
    </div>

  </div>
</template>

<style scoped>

</style>